/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { Grid, Box, Divider } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftSelect from "components/SoftComponents/SoftSelect";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { ErrorMessage } from "formik";

// Custom
import { useState } from "react";
import ImageUploadMenu from "modules/inspections/utils/ImageUploadMenu";
import { deviceDisplay } from "modules/inspections/utils/display";

function DeviceNotes({ formData, deviceLayout, userPermissions }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setFieldValue, handleChange, handleBack, handleCancel },
        custom: { fetchDevices, activeStep, isLastStep, user, all_devices, deviceOptions, maintenanceOptions }
    } = formData;
    const { devicePrimary, deviceSecondary, title, description, recordedAt, priority, maintenance_tag, recordType } = formField;

    const [showDevicePrimary, setShowDevicePrimary] = useState(true);
    const [showDeviceSecondary, setShowDeviceSecondary] = useState(true);

    const canEditDate = userPermissions.indexOf('operations-inspections:delete') >= 0 || userPermissions.indexOf('operations-assignments:delete') >= 0;
    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;
    const factoryAcceptanceTester = userPermissions.includes("operations:fat");

    const priorityOptions = [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" }
    ];

    return (
        <>
            <SoftBox px={{ xxs: 1, sm: 3 }} pt={{ xxs: 2, sm: 3 }} pb={{ xxs: 0, xl: 3 }}>
                <SoftBox mb={2} display="flex" flexDirection="column" alignItems="center">
                    <SoftTypography variant="h4" fontWeight="bold" textAlign="center">
                        Notes & Image Upload
                    </SoftTypography>
                    <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                        Record the information and images of the site.
                    </SoftTypography>
                </SoftBox>
                <Divider />
                <SoftBox mt={1}>
                    <SoftBox display="flex" flex={1} flexDirection='column'>
                        <SoftBox flex={{ xxs: values?.['deviceSecondary-uuid']?.trim().length > 0 ? 6 : 12, sm: 12 }}>
                            {deviceDisplay(values, isLastStep, 'devicePrimary', fetchDevices?.[values['devicePrimary-uuid']], showDevicePrimary, setShowDevicePrimary, showAdvancedDeviceLevel)}
                        </SoftBox>
                        {values?.['deviceSecondary-uuid']?.trim().length > 0 && (
                            <>
                                <SoftBox flex={{ sm: 12 }} sx={{ mt: { xxs: 1.5, sm: 1 }, mb: { xxs: 0, sm: 1 }, display: { xxs: 'none', sm: 'block' } }}>
                                    <Divider orientation="horizontal" />
                                </SoftBox>
                                <Box sx={{ width: 0, display: { xxs: 'flex', sm: 'none' }, mx: { xxs: 2, sm: 0 } }}>
                                    <Divider orientation="vertical" flexItem />
                                </Box>
                                <SoftBox flex={{ xxs: 6, sm: 12 }}>
                                    {deviceDisplay(values, isLastStep, 'deviceSecondary', fetchDevices?.[values['deviceSecondary-uuid']], showDeviceSecondary, setShowDeviceSecondary, showAdvancedDeviceLevel)}
                                </SoftBox>
                            </>
                        )}
                    </SoftBox>
                    <SoftBox mt={1} mb={2}>
                        <Divider />
                    </SoftBox>
                    <Grid container spacing={1}>
                        <Grid item xxs={12} sm={canEditDate && maintenanceOptions.length > 0 ? 8 : 12} lg={canEditDate || maintenanceOptions.length > 0 ? 12 : 8}>
                            <FormField
                                type={title.type}
                                label={title.label}
                                name={title.name}
                                value={values.title}
                                placeholder={`${values["inspection_type"]}`}
                                error={errors.title && touched.title}
                                success={(values.title.length > 0 || values.inspection_type.length > 0) && !errors.title}
                                onChange={handleChange}
                                sx={{ ".MuiInputBase-input::placeholder": { color: "#495057 !important", opacity: 1 } }}
                            />
                        </Grid>
                        <Grid item xxs={12} sm={(canEditDate && maintenanceOptions.length > 0) ? 4 : (canEditDate || maintenanceOptions.length > 0 ? 6 : 12)} lg={(canEditDate && maintenanceOptions.length > 0) ? 4 : (canEditDate || maintenanceOptions.length > 0 ? 6 : 4)}>
                            <SoftBox mb={1.5}>
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="flex" flexDirection="column">
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                    >
                                        {priority.label}
                                    </SoftTypography>
                                </SoftBox>
                                <SoftSelect
                                    name={priority.name}
                                    value={priorityOptions.find(opt => opt.value == values.priority) || { value: "medium", label: "Medium" }}
                                    onChange={(selected) => {
                                        // console.log(selected)
                                        setFieldValue(priority.name, selected ? selected.value : "medium");
                                    }}
                                    options={priorityOptions}
                                />
                            </SoftBox>
                        </Grid>
                        {maintenanceOptions.length > 0 && (
                            <Grid item xxs={12} sm={6} lg={canEditDate ? 4 : 6}>
                                <SoftBox mb={1.5}>
                                    <SoftBox mb={1} ml={0.5} lineHeight={0} display="flex" flexDirection="column">
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            {maintenance_tag.label}
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftSelect
                                        name={maintenance_tag.name}
                                        value={maintenanceOptions.find(opt => opt.label === values.maintenance_tag) || "N/A"}
                                        onChange={(selected) => {
                                            // console.log(selected)
                                            setFieldValue(maintenance_tag.name, selected ? selected.value : "N/A");
                                        }}
                                        options={maintenanceOptions}
                                        isClearable={true}
                                        placeholder={maintenance_tag.placeholder}
                                    />
                                </SoftBox>
                            </Grid>
                        )}
                        {canEditDate && (
                            <Grid item xxs={factoryAcceptanceTester ? 9 : 12} sm={factoryAcceptanceTester ? 4.5 : 6} lg={factoryAcceptanceTester ? (maintenanceOptions.length > 0 ? 3 : 5) : (maintenanceOptions.length > 0 ? 4 : 6)}>
                                <FormField
                                    type={recordedAt.type}
                                    label={recordedAt.label}
                                    name={recordedAt.name}
                                    value={values.recordedAt}
                                    placeholder={`${values["recordedAt"]}`}
                                    onChange={handleChange}
                                    sx={{ ".MuiInputBase-input::placeholder": { color: "#495057 !important", opacity: 1 } }}
                                />
                            </Grid>
                        )}
                        {factoryAcceptanceTester && (
                            <Grid item xxs={3} sm={1.5} lg={1}>
                                <FormField
                                    type={recordType.type}
                                    label={recordType.label}
                                    name={recordType.name}
                                    value={values.recordType}
                                    placeholder={`${values["recordType"]}`}
                                    onChange={handleChange}
                                    alignment="center"
                                    border={false}
                                />
                            </Grid>
                        )}
                        <Grid item xxs={12} lg={12}>
                            <FormField
                                type={description.type}
                                label={description.label}
                                name={description.name}
                                value={values.description}
                                placeholder={description.placeholder}
                                error={errors?.description && touched?.description}
                                success={values?.description.length > 0 && !errors?.description}
                                onChange={handleChange}

                                multiline
                                rows={3}
                            />
                        </Grid>
                        {values["deviceSecondary-uuid"]?.trim().length > 0 ? (
                            <>
                                <Grid item xxs={12} lg={6}>
                                    <SoftBox mb={1} ml={0.5} lineHeight={0} display="flex" flexDirection="column">
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            Original Device / Site Images
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox mb={0.75}>
                                        <SoftTypography component="div" variant="caption" color="error">
                                            <ErrorMessage name={devicePrimary.images.name} />
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox>
                                        <ImageUploadMenu
                                            field={devicePrimary.images}
                                            values={values["devicePrimary-images"]}
                                            setFieldValue={setFieldValue}
                                            deviceIndex={0}
                                        />
                                    </SoftBox>
                                </Grid>
                                <Grid item xxs={12} lg={6}>
                                    <SoftBox mt={{ xxs: 3, lg: 0 }} mb={1} ml={0.5} lineHeight={0} display="flex" flexDirection="column">
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                            textTransform="capitalize"
                                        >
                                            Rectified Device / Site Images
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox mb={0.75}>
                                        <SoftTypography component="div" variant="caption" color="error">
                                            <ErrorMessage name={deviceSecondary.images.name} />
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox>
                                        <ImageUploadMenu
                                            field={deviceSecondary.images}
                                            values={values["deviceSecondary-images"]}
                                            setFieldValue={setFieldValue}
                                            deviceIndex={1}
                                        />
                                    </SoftBox>
                                </Grid>
                            </>
                        ) : (
                            <Grid item xxs={12} lg={12}>
                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="flex" flexDirection="column">
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                        textTransform="capitalize"
                                    >
                                        Original Device / Site Images
                                    </SoftTypography>
                                </SoftBox>
                                <SoftBox mb={0.75}>
                                    <SoftTypography component="div" variant="caption" color="error">
                                        <ErrorMessage name={devicePrimary.images.name} />
                                    </SoftTypography>
                                </SoftBox>
                                <SoftBox>
                                    <ImageUploadMenu
                                        field={devicePrimary.images}
                                        values={values["devicePrimary-images"]}
                                        setFieldValue={setFieldValue}
                                        deviceIndex={0}
                                    />
                                </SoftBox>

                            </Grid>
                        )}
                    </Grid>
                </SoftBox>
            </SoftBox>
            <SoftBox mt={1}>
                <Divider />
                <SoftBox sx={{ background: "#f9f9f9", borderRadius: "0 0 16px 16px" }} display="flex" justifyContent="space-between" p={1}>
                    <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                        {activeStep === 0 ? "cancel" : "back"}
                    </SoftButton>
                    <SoftButton
                        disabled={isSubmitting}
                        type="submit"
                        variant="gradient"
                        color="dark"
                    >
                        {isLastStep ? "submit" : "next"}
                    </SoftButton>
                </SoftBox>
            </SoftBox>
        </>
    );
}

// typechecking props for DeviceNotes
DeviceNotes.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
};

export default DeviceNotes;
